<template>
  <div class="p-6 bg-white dark:bg-gray-900 dark:bg-opacity-90 dark:border-gray-600 border shadow-sm rounded-xl">
    <h2 class="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
      Vue 2.7.16 Composition API Demo
    </h2>
    
    <!-- Counter Example -->
    <div class="mb-8">
      <h3 class="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Reactive Counter</h3>
      <div class="flex items-center space-x-4">
        <button 
          @click="decrement"
          class="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
        >
          -
        </button>
        <span class="text-2xl font-bold text-gray-900 dark:text-gray-100 min-w-[3rem] text-center">
          {{ count }}
        </span>
        <button 
          @click="increment"
          class="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
        >
          +
        </button>
      </div>
      <p class="text-sm text-gray-600 dark:text-gray-400 mt-2">
        Double count: {{ doubleCount }}
      </p>
    </div>

    <!-- Computed Properties -->
    <div class="mb-8">
      <h3 class="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Computed Properties</h3>
      <div class="space-y-2">
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Count is {{ countParity }}
        </p>
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Count squared: {{ countSquared }}
        </p>
      </div>
    </div>

    <!-- Watchers -->
    <div class="mb-8">
      <h3 class="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Watchers</h3>
      <div class="space-y-2">
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Previous count: {{ previousCount }}
        </p>
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Count changes: {{ changeCount }}
        </p>
      </div>
    </div>

    <!-- Lifecycle Hooks -->
    <div class="mb-8">
      <h3 class="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Lifecycle Hooks</h3>
      <div class="space-y-2">
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Component mounted: {{ isMounted ? 'Yes' : 'No' }}
        </p>
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Time since mount: {{ timeSinceMount }}s
        </p>
      </div>
    </div>

    <!-- Date Picker Demo -->
    <div class="mb-8">
      <h3 class="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Modern Date Picker</h3>
      <modern-date-picker-composition 
        v-model="selectedDate"
        label="Select Date"
        placeholder="Choose a date"
      />
      <p class="text-sm text-gray-600 dark:text-gray-400 mt-2">
        Selected: {{ selectedDate ? selectedDate.toLocaleDateString() : 'None' }}
      </p>
    </div>

    <!-- Vuex Integration -->
    <div class="mb-8">
      <h3 class="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Vuex Integration</h3>
      <div class="space-y-2">
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Is Authenticated: {{ isAuthenticated ? 'Yes' : 'No' }}
        </p>
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Loading State: {{ isLoading ? 'Loading...' : 'Ready' }}
        </p>
        <button 
          @click="toggleAuth"
          class="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          {{ isAuthenticated ? 'Logout' : 'Login' }}
        </button>
      </div>
    </div>

    <!-- Router Integration -->
    <div class="mb-8">
      <h3 class="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Router Integration</h3>
      <div class="space-y-2">
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Current Route: {{ currentRoute }}
        </p>
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Route Params: {{ routeParams }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import ModernDatePickerComposition from './ModernDatePickerComposition.vue'

export default {
  name: 'CompositionApiDemo',
  components: {
    ModernDatePickerComposition
  },
  setup() {
    const store = useStore()
    const route = useRoute()

    // Reactive data
    const count = ref(0)
    const previousCount = ref(0)
    const changeCount = ref(0)
    const isMounted = ref(false)
    const mountTime = ref(null)
    const selectedDate = ref(null)

    // Computed properties
    const doubleCount = computed(() => count.value * 2)
    const countParity = computed(() => count.value % 2 === 0 ? 'even' : 'odd')
    const countSquared = computed(() => count.value ** 2)
    const timeSinceMount = computed(() => {
      if (!mountTime.value) return 0
      return Math.floor((Date.now() - mountTime.value) / 1000)
    })

    // Vuex computed properties
    const isAuthenticated = computed(() => store.state.isAuthenticated)
    const isLoading = computed(() => store.state.isLoading)

    // Router computed properties
    const currentRoute = computed(() => route.name)
    const routeParams = computed(() => route.params)

    // Methods
    const increment = () => {
      count.value++
    }

    const decrement = () => {
      count.value--
    }

    const toggleAuth = () => {
      if (isAuthenticated.value) {
        store.commit('logout')
      } else {
        store.commit('authenticate')
      }
    }

    // Watchers
    watch(count, (newVal, oldVal) => {
      previousCount.value = oldVal
      changeCount.value++
      console.log(`Count changed from ${oldVal} to ${newVal}`)
    })

    // Lifecycle hooks
    onMounted(() => {
      isMounted.value = true
      mountTime.value = Date.now()
      console.log('Component mounted with Composition API!')
    })

    onBeforeUnmount(() => {
      console.log('Component will unmount')
    })

    return {
      // Reactive data
      count,
      previousCount,
      changeCount,
      isMounted,
      selectedDate,
      
      // Computed
      doubleCount,
      countParity,
      countSquared,
      timeSinceMount,
      isAuthenticated,
      isLoading,
      currentRoute,
      routeParams,
      
      // Methods
      increment,
      decrement,
      toggleAuth
    }
  }
}
</script> 